import { llm } from '@cotera/api';
import type { LlmCallSettings } from './llm-input-types';
import { mergeProviderOptions } from './model-input-base';

/**
 * Reads a numeric tuning override from the environment, server-side only.
 *
 * Guarded because this module is also reachable from client bundles, where
 * `process` does not exist.
 */
function envNumber(name: string, fallback: number | undefined) {
  const raw = typeof process !== 'undefined' ? process.env?.[name] : undefined;
  if (raw === undefined || raw === '') {
    return fallback;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Sampling overrides for LunaRoute-served models (Cotera Unlimited).
 *
 * Keyed on `provider === 'LunaRoute'`, not on a model id, so it survives model
 * swaps — the tier now serves GLM 5.3 Vision, and these settings carried over
 * without a change. Everything measured below was measured on 5.2, the model
 * the tier served when the runaway was diagnosed. The root cause is a sampling
 * parameter rather than a model defect, so it is expected to apply to any
 * OpenAI-shaped model behind this gateway; re-measure on the current model
 * before loosening anything.
 *
 * GLM 5.2 Vision degenerates inside long agent loops: it produces coherent
 * analysis, then collapses into an endless synonym chain — "constraint restraint
 * check control rule regulation law statute ordinance decree ..." — and cycles
 * that chain until it exhausts its token budget. Observed in production at 27k
 * output tokens against a median of 83.
 *
 * It is not confined to the final answer. The same collapse happens *inside tool
 * call arguments*: one observed Google search query grew to ~20k characters of
 * synonym chain and came back HTTP 414 (URI too long). So this is a generation
 * failure that lands wherever the model happens to be writing, not a broken tool
 * template — ordinary tool calls on this model work fine.
 *
 * **The cause is `frequency_penalty`, and the fix is to stop sending it.**
 * Every ATC caller hard-codes `frequencyPenalty: 0.5` and `presencePenalty: 0.5`
 * (atc.ts, atc.controller.ts, llm.jobs.ts) because those were sensible defaults
 * for the GPT-3.5-era models this code was first written against. Frequency
 * penalty subtracts from a token's logit in proportion to how often it has
 * already appeared, so in a long generation the common words — including the
 * ones that end a sentence and hand off to EOS — are pushed down until the only
 * reachable tokens are ever-rarer synonyms. That is a positive feedback loop,
 * which is exactly why the collapse only appears in long outputs deep in agent
 * loops and never in short ones.
 *
 * This also explains why the same model is well behaved under Claude Code: the
 * Anthropic Messages API has no `frequency_penalty` or `presence_penalty` field
 * at all, so an Anthropic-shaped client cannot send the thing that breaks it.
 *
 * Re-measured against the gateway on a reconstructed agent loop (Coco system
 * prompt, web-search tool, 8 rounds of tool_call/tool_result ≈ 24k prompt
 * tokens, streaming), 8 samples per arm. "degen" counts runs that hit the token
 * ceiling or whose output tail is >60% hapax; healthy prose sits near 0.2:
 *
 *   arm                          degen  avg_out  hit `length`  hapax
 *   t.5 p.9 f.5 pp.5  (was prod)   8/8     6000           8/8   0.47
 *   t.5 p.9 f.5 pp1.5 (was prod)   8/8     6000           8/8   0.79
 *   t.5 p.9     f.5                7/8     5804           7/8   0.33
 *   t.5 p.9         pp.5           0/8     4538           0/8   0.21
 *   t.5 p.9                        0/8     4883           0/8   0.19
 *   t1.0 (what Claude Code sends)  0/8     5091           0/8   0.19
 *
 * Every arm carrying a frequency penalty degenerates; every arm without one
 * finishes `stop` with a complete answer, including the arm that still carries
 * `presence_penalty 0.5`. Confirmed on the unforced agentic path (the model
 * choosing its own response length, 8 samples at each of 24k and 60k prompt
 * tokens): 6/32 runaway with the frequency penalty, 0/32 without.
 *
 * Note the two previously-shipped mitigations are the two *worst* rows. Raising
 * the presence penalty to 1.5 stacked on top of the frequency penalty rather
 * than replacing it, and made the collapse more florid rather than stopping it.
 *
 * Confirmed once more at the real 16k cap rather than the 6k the sweep used, so
 * a runaway has full rope, comparing what shipped before against what this
 * module now emits (8 samples each):
 *
 *   before  t.5 p.9 f.5 pp1.5   8/8 degen   avg 15904 tok   7/8 hit `length`
 *   after   t.5 p.9             0/8 degen   avg  4486 tok   0/8 hit `length`
 *
 * Every value is env-overridable so the mitigation can be re-tuned in an
 * environment without a code change or redeploy. An empty or unparseable value
 * falls back to the default listed below:
 *
 *   COTERA_LUNAROUTE_FREQUENCY_PENALTY   default unset — NOT sent (see above)
 *   COTERA_LUNAROUTE_PRESENCE_PENALTY    default unset — NOT sent
 *   COTERA_LUNAROUTE_TEMPERATURE         default unset (caller's value wins)
 *   COTERA_LUNAROUTE_MAX_OUTPUT_TOKENS   default 16000
 *
 * The token cap stays as a circuit breaker rather than a tuning parameter: the
 * model is declared with maxOutputTokens 131072, which would bound any future
 * runaway at roughly ten minutes and one held ATC semaphore slot. 16k bounds it
 * to about a minute while staying far above the ~1.3k that healthy runs use.
 */
const LUNAROUTE_TUNING = {
  frequencyPenalty: envNumber('COTERA_LUNAROUTE_FREQUENCY_PENALTY', undefined),
  presencePenalty: envNumber('COTERA_LUNAROUTE_PRESENCE_PENALTY', undefined),
  temperature: envNumber('COTERA_LUNAROUTE_TEMPERATURE', undefined),
  maxOutputTokens: envNumber('COTERA_LUNAROUTE_MAX_OUTPUT_TOKENS', 16000),
};

/**
 * Apply Cotera model-specific rules to sampling / provider options before
 * {@link streamText} (ported from server `transformGenerateParams`).
 */
export function transformCallSettings(
  modelId: string,
  baseParams: Omit<LlmCallSettings, 'providerOptions'>
): LlmCallSettings {
  const model = llm.getModel(modelId);
  if (model === undefined) {
    return baseParams;
  }

  let result: LlmCallSettings = { ...baseParams };

  if (model.provider === 'Google') {
    result = {
      ...result,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
    };
  }

  if (model.provider === 'LunaRoute') {
    result = {
      ...result,
      // Both penalties are *replaced*, never merged with the caller's value.
      // A `??` fallback here would be dead code: every ATC caller hard-codes
      // 0.5 for both, so the caller's value always wins and the tuning is never
      // reached. That trap is what kept the previous mitigation from taking
      // effect. Defaults are undefined, so nothing is sent unless an operator
      // sets the env var back on.
      frequencyPenalty: LUNAROUTE_TUNING.frequencyPenalty,
      presencePenalty: LUNAROUTE_TUNING.presencePenalty,
      temperature: result.temperature ?? LUNAROUTE_TUNING.temperature,
      // The cap is deliberately NOT `??` — a runaway is exactly the case where
      // the caller's generous limit must not win.
      maxOutputTokens:
        LUNAROUTE_TUNING.maxOutputTokens === undefined
          ? result.maxOutputTokens
          : Math.min(
              result.maxOutputTokens ?? LUNAROUTE_TUNING.maxOutputTokens,
              LUNAROUTE_TUNING.maxOutputTokens
            ),
    };

    // Required for `Output.object` to become a real `response_format:
    // json_schema` request. Without it the AI SDK's OpenAI provider falls back
    // to asking for JSON in the prompt, which is ordinary free-form generation —
    // the exact path this model runs away on. Measured against the gateway:
    //
    //   tools, no schema        110-132 tok, no object
    //   schema, no tools        682-2196 tok, valid object 5/5
    //   tools + schema          462-559 tok, valid object 5/5
    //   free-form               up to the token ceiling, 60% runaway
    //
    // Constrained decoding is the one configuration that is both bounded and
    // correct, and it composes fine with tool calling.
    result.providerOptions = mergeProviderOptions(result.providerOptions, {
      openai: {
        structuredOutputs: true,
        strictJsonSchema: true,
      },
    });

    return result;
  }

  if (model.provider === 'Anthropic') {
    // Newer Anthropic models drop `temperature`/`topP` - sending them 400s
    // and the stream returns empty.
    if (
      modelId === 'claude-opus-4-7' ||
      modelId === 'claude-sonnet-5' ||
      modelId === 'claude-opus-5-5'
    ) {
      result = {
        ...result,
        temperature: undefined,
        topP: undefined,
      };
    } else if (result.temperature !== undefined && result.topP !== undefined) {
      result = {
        ...result,
        topP: undefined,
      };
    }

    // Opus 5.5 is newer than the pinned @ai-sdk/anthropic, which treats unknown
    // models as unable to do native structured output and falls back to a
    // forced `json` tool (`tool_choice: {type: "tool"}`) — a 400 on Opus 5.5.
    // It also defaults unknown models to `max_tokens: 4096`, too tight now that
    // thinking is always on. Pin both until the SDK learns the model.
    if (modelId === 'claude-opus-5-5') {
      result = {
        ...result,
        maxOutputTokens: result.maxOutputTokens ?? model.maxOutputTokens,
      };
      result.providerOptions = mergeProviderOptions(result.providerOptions, {
        anthropic: {
          structuredOutputMode: 'outputFormat',
        },
      });
    }
  }

  if (model.provider === 'AzureFoundry' && !model.supports.reasoningText) {
    const { maxOutputTokens: _maxOutputTokens, ...rest } = result;
    return rest;
  }

  if (!model.supports.reasoningText) {
    return result;
  }

  // Azure models served over the Responses API (see `Model.azureApi`). The AI
  // SDK reads their provider options under `azure`, not `openai`, and maps
  // `maxOutputTokens` to `max_output_tokens` itself. `forceReasoning` makes it
  // drop the sampling params, since it doesn't recognise the deployment name as
  // a reasoning model; the penalties are cleared here because Responses has no
  // such fields.
  if (model.provider === 'AzureFoundry' && model.azureApi === 'responses') {
    return {
      ...result,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
      providerOptions: mergeProviderOptions(result.providerOptions, {
        azure: {
          forceReasoning: true,
          strictJsonSchema: true,
        },
      }),
    };
  }

  if (model.provider === 'OpenAI' || model.provider === 'AzureFoundry') {
    const {
      maxOutputTokens,
      temperature: _temperature,
      ...otherParams
    } = baseParams;

    const isGpt5 =
      modelId.startsWith('gpt-5') || modelId.startsWith('azure-gpt-5');
    // The pinned @ai-sdk/openai only recognises o-series and gpt-5 deployment
    // names as reasoning models, so for `gpt-6-*` it would forward
    // `presence_penalty`/`frequency_penalty` and `max_tokens`, all of which
    // GPT-6 rejects. `forceReasoning` makes the SDK strip them as it does for
    // GPT-5.
    //
    // GPT-6 on /chat/completions also refuses function tools unless
    // `reasoning_effort` is 'none' (reasoning + tools needs /responses), and
    // unlike GPT-5.5/5.6 its default effort isn't 'none'. Pin it so tool calls
    // work — the same zero-reasoning behaviour 5.5/5.6 get by default here.
    const isGpt6 = modelId.startsWith('azure-gpt-6');
    if (isGpt5 || isGpt6) {
      result = {
        ...otherParams,
        temperature: 1,
      };
      if (maxOutputTokens !== undefined) {
        result.providerOptions = {
          openai: {
            max_completion_tokens: maxOutputTokens,
          },
        };
      }
    } else if (maxOutputTokens !== undefined) {
      result = {
        ...otherParams,
        providerOptions: {
          openai: {
            max_completion_tokens: maxOutputTokens,
          },
        },
      };
    } else {
      result = otherParams;
    }

    result.providerOptions = mergeProviderOptions(result.providerOptions, {
      openai: {
        structuredOutputs: true,
        strictJsonSchema: true,
        ...(isGpt6 ? { forceReasoning: true, reasoningEffort: 'none' } : {}),
      },
    });
  }

  return result;
}
